import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { FunctionComponent } from "react";
import { useCallback, useMemo, useState } from "react";

import { getDataFromResponse, handleApiError } from "~/api/axiosUtils.ts";
import Card from "~/components/Card";
import { SwitchPlain } from "~/components/formElements/Switch/Switch.tsx";
import { useAggregatedDataV2 } from "~/hooks/useAggregatedDataV2.ts";
import { useAllCareerLevels } from "~/modules/humanResources/api/careerLevel/careerLevelQueries.ts";
import { CareerLevelId } from "~/modules/humanResources/api/careerLevel/careerLevelTypes.ts";
import { useAllStaffMembers } from "~/modules/humanResources/api/staffMember/staffMemberQueries.ts";
import { useAllProjects } from "~/modules/project/api/project/projectQueries.ts";
import {
	recalculateTeamUtilisationReport,
} from "~/modules/reports/api/utilisationReport/utilisationReportApiDispatchers.ts";
import { utilisationReportQueries } from "~/modules/reports/api/utilisationReport/utilisationReportQueries.ts";
import DownloadTeamUtilisationReportExcelButton
	from "~/modules/reports/components/GlobalReportsView/components/TeamUtilisationReportSection/components/DownloadTeamUtilisationReportExcelButton";
import TeamUtilisationTable
	from "~/modules/reports/components/GlobalReportsView/components/TeamUtilisationReportSection/components/TeamUtilisationTable";
import type {
	ResolvedGroup,
} from "~/modules/reports/components/GlobalReportsView/components/TeamUtilisationReportSection/components/TeamUtilisationTable/TeamUtilisationTable.tsx";
import UtilisationReportRefreshControl
	from "~/modules/reports/components/GlobalReportsView/components/UtilisationReportSection/components/UtilisationReportRefreshControl";
import LoadingPage from "~/pages/LoadingPage.tsx";
import { byObjectProperty } from "~/utils/sortFunctions.ts";

type Props = {
	year: string;
};

const CAREER_LEVEL_GROUP_ORDER: CareerLevelId[] = [
	CareerLevelId.SeniorManager,
	CareerLevelId.Manager,
	CareerLevelId.SeniorConsultant,
	CareerLevelId.Consultant,
	CareerLevelId.Analyst,
	CareerLevelId.IT,
	CareerLevelId.Student,
	CareerLevelId.ManagingDirector,
	CareerLevelId.Partner,
];

const groupOrderIndex = (careerLevelId: string) => {
	const index = CAREER_LEVEL_GROUP_ORDER.indexOf(careerLevelId as CareerLevelId);
	return index === -1 ? CAREER_LEVEL_GROUP_ORDER.length : index;
};

const TeamUtilisationReportSection: FunctionComponent<Props> = ({ year }) => {
	const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
	const [showSalesAndInternal, setShowSalesAndInternal] = useState<boolean>(true);
	const [showProjects, setShowProjects] = useState<boolean>(true);
	const queryClient = useQueryClient();

	const { data, options } = useAggregatedDataV2({
		careerLevels: useAllCareerLevels(),
		staffMembers: useAllStaffMembers(),
		projects: useAllProjects(),
		report: useQuery(utilisationReportQueries.team(year || "")),
	});

	const { careerLevels, staffMembers, projects, report } = data;
	const { isLoading, loadingPercent } = options;

	const handleRefresh = useCallback(async () => {
		setIsRefreshing(true);
		try {
			const freshReport = getDataFromResponse(await recalculateTeamUtilisationReport(year));
			queryClient.setQueryData(utilisationReportQueries.team(year).queryKey, freshReport);
		} catch (error) {
			handleApiError(error);
		} finally {
			setIsRefreshing(false);
		}
	}, [queryClient, year]);

	const resolvedGroups = useMemo<ResolvedGroup[]>(() => {
		if (!report || !staffMembers || !careerLevels || !projects) return [];

		const careerLevelNameById = new Map(careerLevels.map((careerLevel) => [careerLevel.id, careerLevel.displayName]));
		const staffNameById = new Map(staffMembers.map((staffMember) => [staffMember.id, staffMember.fullName]));
		const projectTitleById = new Map(projects.map((project) => [project.id, project.title]));

		const groups: ResolvedGroup[] = report.careerLevelGroups.map((group) => {
			const members = group.members.map((member) => {
				const projectRows = member.rows
					.filter((row) => row.type === "project")
					.map((row) => ({ ...row, label: projectTitleById.get(row.projectId ?? "") ?? "Projekt" }))
					.sort(byObjectProperty("label"));

				return {
					staffMemberId: member.staffMemberId,
					userId: member.userId,
					name: staffNameById.get(member.staffMemberId) ?? "Unbekannt",
					mainRow: member.rows.find((row) => row.type === "main"),
					projectRows,
					internalRow: member.rows.find((row) => row.type === "internal"),
					salesRow: member.rows.find((row) => row.type === "sales"),
				};
			}).sort(byObjectProperty("name"));

			return {
				careerLevelId: group.careerLevelId,
				displayName: careerLevelNameById.get(group.careerLevelId) ?? "",
				members,
			};
		});

		return groups.sort((a, b) => groupOrderIndex(a.careerLevelId) - groupOrderIndex(b.careerLevelId));
	}, [report, careerLevels, staffMembers, projects]);

	return <div className="grid size-full grid-rows-[auto_1fr] gap-y-2 overflow-hidden pb-8 pt-4">
		{isLoading && <LoadingPage pcent={loadingPercent} />}
		{!isLoading && report && <>
			<Card>
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-x-6">
						<SwitchPlain checked={showSalesAndInternal}
									 name="showSalesAndInternal"
									 label="Sales & Intern inkludieren"
									 inputSize="sm"
									 onChange={setShowSalesAndInternal} />
						<SwitchPlain checked={showProjects}
									 name="showProjects"
									 inputSize="sm"
									 label="Projekte anzeigen"
									 onChange={setShowProjects} />
					</div>
					<div className="flex items-center gap-x-3">

						<UtilisationReportRefreshControl generatedAt={report.generatedAt}
														 isRefreshing={isRefreshing}
														 onRefresh={handleRefresh}
						buttonSize={"sm"}
						/>
						<DownloadTeamUtilisationReportExcelButton groups={resolvedGroups}
																  months={report.months}
																  year={year} />
					</div>
				</div>
			</Card>
			<TeamUtilisationTable groups={resolvedGroups}
								  months={report.months}
								  showProjects={showProjects}
								  showSalesAndInternal={showSalesAndInternal} />
		</>}
	</div>;
};

export default TeamUtilisationReportSection;
